'use client'

import type { ReactNode } from 'react'
import {
  Debug as Bug,
  Security as Shield,
  AiGenerate as Sparkles,
  Growth as TrendingUp,
  type CarbonIconType as LucideIcon,
} from '@carbon/icons-react'

import { cn } from '@/lib/utils'
import { PanelDots } from '@/components/fx/panel-dots'
import { ChromaText } from '@/components/ui/chroma-text'

import type { UpdateType } from './data'

/* ─── Shared kit for the product-updates family ────────────────────────────
   The index and every version page are the same object at two zoom levels, so
   they share one device — the release ledger: a monospace version figure
   hanging in a left margin, hairline rules between entries, and no tinted
   chips anywhere. Only the head is componentised here; the ledger rows differ
   enough per page to stay local. ── */

export const HAIRLINE = 'border-border/70'

/* Update types. The old map hardcoded light-only pairs — `bg-blue-50
   text-blue-700 border-blue-200` and friends — which rendered as pale blocks
   of near-black text sitting on the dark canvas. These are text tones with a
   dark counterpart, and the chip backgrounds are gone entirely. */
export const TYPE_META: Record<
  Exclude<UpdateType, 'All'>,
  { icon: LucideIcon; label: string; short: string; tone: string }
> = {
  'New Feature': {
    icon: Sparkles,
    label: 'New feature',
    short: 'New',
    tone: 'text-emerald-600 dark:text-primary-active',
  },
  Improvement: {
    icon: TrendingUp,
    label: 'Improvement',
    short: 'Improved',
    tone: 'text-sky-600 dark:text-sky-400',
  },
  'Bug Fix': {
    icon: Bug,
    label: 'Bug fix',
    short: 'Fixed',
    tone: 'text-amber-600 dark:text-amber-500',
  },
  Security: {
    icon: Shield,
    label: 'Security',
    short: 'Security',
    tone: 'text-violet-600 dark:text-violet-400',
  },
}

/**
 * Page head for the family. The animated dot matrix runs as a field over the
 * whole head rather than inside a panel card: masked so it is dense under the
 * header and dissolved to nothing before the content starts, which is what
 * keeps it from ending on a hard edge the way a card would.
 */
export function UpdatesMasthead({
  eyebrow,
  title,
  deck,
  above,
  children,
}: {
  eyebrow: ReactNode
  title: ReactNode
  deck?: ReactNode
  /** Back link or breadcrumb, sits above the eyebrow. */
  above?: ReactNode
  /** Record band or anything else that closes the head. */
  children?: ReactNode
}) {
  return (
    <header
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      {/* Written out in full rather than interpolated — Tailwind scans source
          text for complete class strings, so a template-built arbitrary value
          never gets generated. */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 z-0',
          '[mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.55)_0%,black_34%,rgba(0,0,0,0.35)_72%,transparent_100%)]',
          '[-webkit-mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.55)_0%,black_34%,rgba(0,0,0,0.35)_72%,transparent_100%)]'
        )}
      >
        <PanelDots className="text-zinc-900 dark:text-white" />
      </div>

      <div className="container relative z-10 mx-auto">
        <div className="pb-12 pt-[calc(var(--header-h)+2.5rem)] sm:pb-16 sm:pt-[calc(var(--header-h)+4rem)]">
          {above}

          <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            <span
              aria-hidden
              className="h-px w-6 shrink-0 bg-emerald-600/60 dark:bg-primary-active/50"
            />
            {eyebrow}
          </p>

          <h1 className="font-heading mt-6 max-w-4xl text-[30px]/[1.15] font-normal tracking-tight text-foreground sm:text-[40px]/[1.14] md:text-5xl/[1.12] lg:text-[54px]/[1.1]">
            <ChromaText>{title}</ChromaText>
          </h1>

          {deck && (
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg md:text-[19px]">
              {deck}
            </p>
          )}
        </div>
      </div>

      {children}
    </header>
  )
}

/**
 * Record band — the full-bleed rule with fields sitting in the container, so
 * the first field lines up under the headline. Same construction the article
 * and story heads use.
 */
export function RecordBand({
  fields,
}: {
  fields: { label: string; value: ReactNode }[]
}) {
  if (fields.length === 0) return null

  return (
    <div className={cn('relative z-10 border-y', HAIRLINE)}>
      <div className="container mx-auto">
        <dl
          className={cn(
            'grid grid-cols-2 gap-x-6 gap-y-5 py-5',
            'lg:grid-cols-4 lg:gap-x-px lg:gap-y-0 lg:bg-border/70 lg:py-0'
          )}
        >
          {fields.map((f) => (
            <div
              key={f.label}
              className="lg:bg-background lg:py-6 lg:pl-5 lg:pr-6 lg:first:pl-0"
            >
              <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                {f.label}
              </dt>
              <dd className="mt-1.5 text-sm text-foreground sm:text-[15px]">
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

/**
 * Filter / jump button. Solid quiet fill when active rather than the old
 * `bg-foreground text-background` slab or a tinted pill.
 */
export function ToggleChip({
  active,
  onClick,
  href,
  children,
  className,
}: {
  active?: boolean
  onClick?: () => void
  href?: string
  children: ReactNode
  className?: string
}) {
  const cls = cn(
    'inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
    'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
    'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active',
    active
      ? 'bg-muted font-medium text-foreground dark:bg-muted/70'
      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
    className
  )

  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    )
  }

  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  )
}
